import { Download, ArrowRight, Globe, RefreshCw, Code2 } from "lucide-react";

export default function HeroSection({ apiUrl, onDownload }) {

  return (
    <section
      data-testid="hero-section"
      className="pt-28 pb-24 md:pt-36 md:pb-32 px-6 md:px-12 lg:px-24 relative overflow-hidden"
    >
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#FF7300]/[0.04] rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div>
            <div className="animate-fade-in-up">
              <span className="inline-flex items-center gap-2 bg-[#FF7300]/10 text-[#FF7300] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                WordPress Plugin v1.0
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display font-extrabold text-[#09090B] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] mb-6 animate-fade-in-up delay-100"
            >
              Amazon Affiliate
              <br />
              <span className="text-[#FF7300]">Product Importer</span>
            </h1>

            <p className="text-[#52525B] text-base md:text-lg leading-relaxed mb-8 max-w-lg animate-fade-in-up delay-200">
              Paste Amazon links, auto-extract product data, and display
              beautiful product cards on your WordPress site. Prices sync every
              8 hours — no API needed.
            </p>

            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
              <button
                data-testid="hero-download-btn"
                onClick={onDownload}
                className="flex items-center gap-2 bg-[#FF7300] text-white hover:bg-[#E66800] hover:shadow-lg transition-all rounded-md px-8 py-4 font-bold text-lg border-0 cursor-pointer animate-pulse-glow"
              >
                <Download size={20} />
                Download Plugin
              </button>
              <a
                href="#features"
                data-testid="hero-features-link"
                className="flex items-center gap-2 border border-[#E4E4E7] bg-white text-[#09090B] hover:bg-[#F9FAFB] hover:border-[#09090B] transition-all rounded-md px-6 py-4 font-medium no-underline"
              >
                Explore Features
                <ArrowRight size={16} />
              </a>
            </div>

            {/* Stats row */}
            <div className="flex gap-10 mt-12 animate-fade-in-up delay-400">
              {[
                { label: "Domains", value: "3" },
                { label: "Auto-Sync", value: "8h" },
                { label: "License", value: "GPL" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="font-display font-bold text-[#09090B] text-2xl">{stat.value}</div>
                  <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Visual */}
          <div className="hidden lg:block animate-fade-in-up delay-300">
            <div className="relative">
              {/* Code preview card */}
              <div className="bg-[#09090B] rounded-lg p-6 shadow-2xl">
                <div className="flex gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28C840]" />
                </div>
                <pre className="font-mono text-[13px] leading-relaxed text-[#e4e4e7]">
                  <span className="text-[#FF7300]">[affiliate_product</span>{" "}
                  <span className="text-[#93c5fd]">id</span>=
                  <span className="text-[#86efac]">"142"</span>
                  <span className="text-[#FF7300]">]</span>
                  {"\n\n"}
                  <span className="text-[#FF7300]">[affiliate_grid</span>{"\n"}
                  {"  "}<span className="text-[#93c5fd]">category</span>=
                  <span className="text-[#86efac]">"electronics"</span>{"\n"}
                  {"  "}<span className="text-[#93c5fd]">columns</span>=
                  <span className="text-[#86efac]">"3"</span>{"\n"}
                  {"  "}<span className="text-[#93c5fd]">limit</span>=
                  <span className="text-[#86efac]">"6"</span>
                  <span className="text-[#FF7300]">]</span>
                </pre>
              </div>

              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-white border border-[#E4E4E7] rounded-lg px-4 py-3 shadow-md animate-fade-in delay-500">
                <div className="flex items-center gap-2">
                  <Globe size={16} className="text-[#FF7300]" />
                  <span className="text-xs font-semibold text-[#09090B]">3 Amazon Domains</span>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white border border-[#E4E4E7] rounded-lg px-4 py-3 shadow-md animate-fade-in delay-600">
                <div className="flex items-center gap-2">
                  <RefreshCw size={16} className="text-[#2563EB]" />
                  <span className="text-xs font-semibold text-[#09090B]">Auto-Sync 8h</span>
                </div>
              </div>
              <div className="absolute top-1/2 -right-6 bg-white border border-[#E4E4E7] rounded-lg px-4 py-3 shadow-md animate-fade-in delay-700">
                <div className="flex items-center gap-2">
                  <Code2 size={16} className="text-[#16a34a]" />
                  <span className="text-xs font-semibold text-[#09090B]">Gutenberg Block</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
